import React from 'react';
import { connect } from 'react-redux';
import { setLocale } from '../../actions/intl';
import {
  t, PHONE, PHONE_LABEL, WA, initReveal,
  IconPhone, IconWa, IconTick,
  Header, Footer, CallBar
} from './SiteChrome';

/*
 * Halaman Contact — port dari site/contact.html.
 *
 * Halaman ini memimpin dengan NOMOR TELEPON, bukan formulir: orang yang mogok
 * di pinggir jalan sebaiknya menelepon, bukan mengetik. Formulir callback ada
 * di bawahnya sebagai jalur kedua.
 *
 * BACKEND BELUM ADA. Mutation `createCallbackRequest` belum terdaftar di
 * src/data/schema.js, jadi submit akan GAGAL dan menampilkan pesan "telepon
 * saja". Itu disengaja: panel sukses HANYA muncul setelah server menerima.
 * Menampilkan "kami akan menelepon Anda" padahal tidak ada yang tersimpan lebih
 * buruk daripada error yang jujur.
 */

const SERVICES = ['recovery', 'battery', 'tyre', 'fuel', 'lockout', 'unsure'];

// [kunci label, isi <small>, href atau null]
const CHANNELS = [
  ['emergency', PHONE_LABEL, `tel:${PHONE}`, '24/7'],
  ['whatsapp', '⁦+971 50 505 2213⁩', WA, '24/7'],
  ['billing', 'billing@basrecovery.com', 'mailto:billing@basrecovery.com', 'ch.billingHours'],
  ['complaints', 'support@basrecovery.com', 'mailto:support@basrecovery.com', 'ch.complaintsHours'],
  ['provider', null, '/providers', null],
  ['fleet', 'fleet@basrecovery.com', 'mailto:fleet@basrecovery.com', null]
];

const LD_CONTACT = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  url: 'https://basrecovery.com/contact',
  mainEntity: {
    '@type': 'AutoRepair',
    name: 'Bas Recovery',
    url: 'https://basrecovery.com/',
    contactPoint: [
      {
        '@type': 'ContactPoint', telephone: PHONE, contactType: 'emergency',
        availableLanguage: ['en', 'ar'],
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          opens: '00:00', closes: '23:59'
        }
      },
      {
        '@type': 'ContactPoint', email: 'support@basrecovery.com',
        contactType: 'customer support', availableLanguage: ['en', 'ar']
      }
    ]
  }
};

const CREATE_CALLBACK = `mutation ($name: String!, $phone: String!, $where: String!, $service: String, $note: String) {
  createCallbackRequest(name: $name, phone: $phone, where: $where, service: $service, note: $note) {
    status
    id
  }
}`;

class Contact extends React.Component {
  state = {
    navOpen: false,
    name: '', phone: '', where: '', service: 'recovery', note: '',
    errors: {}, sending: false, sent: false, sendError: false
  };

  toggleNav = () => this.setState(s => ({ navOpen: !s.navOpen }));
  closeNav = () => this.setState({ navOpen: false });

  switchLang = () => {
    const next = this.props.currentLocale === 'ar' ? 'en-US' : 'ar';
    document.documentElement.lang = next;
    this.props.setLocale({ locale: next });
  };

  componentDidMount() {
    this.onKey = e => { if (e.key === 'Escape') this.closeNav(); };
    document.addEventListener('keydown', this.onKey);
    this.io = initReveal();
  }
  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKey);
    if (this.io) this.io.disconnect();
  }

  set = field => e => this.setState({ [field]: e.target.value });

  /*
   * Validasi sisi klien, port dari site.js. Ini kenyamanan UX, BUKAN kontrol —
   * server wajib memvalidasi ulang (siapa pun bisa POST langsung). Duplikasi
   * klien/server di sini disengaja.
   */
  validate() {
    const { name, phone, where } = this.state;
    const errors = {};
    if (name.trim().length < 2) errors.name = 'ct.err.name';
    // Terima 0501234567, 971501234567, +971 50 123 4567 — semua jadi 5XXXXXXXX.
    const digits = phone.replace(/[^\d]/g, '').replace(/^971/, '').replace(/^0/, '');
    if (!/^5\d{8}$/.test(digits)) errors.phone = 'ct.err.phone';
    if (where.trim().length < 3) errors.where = 'ct.err.where';
    return errors;
  }

  submit = async e => {
    e.preventDefault();
    const errors = this.validate();
    this.setState({ errors, sendError: false });
    if (Object.keys(errors).length) {
      const first = document.querySelector('[aria-invalid="true"]');
      if (first) first.focus();
      return;
    }

    const { name, phone, where, service, note } = this.state;
    this.setState({ sending: true });
    try {
      // ponytail: fetch langsung ke /graphql yang sama-origin. Tulis sekali,
      // tanpa cache — tidak perlu menyeret apollo HOC ke halaman publik.
      const res = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          query: CREATE_CALLBACK,
          variables: { name, phone, where, service, note }
        })
      });
      const json = await res.json();
      const ok = res.ok && !json.errors &&
        json.data && json.data.createCallbackRequest &&
        json.data.createCallbackRequest.status === 'success';
      if (!ok) throw new Error('callback rejected');
      this.setState({ sending: false, sent: true }, () => {
        if (this.okPanel) this.okPanel.focus();
      });
    } catch (err) {
      this.setState({ sending: false, sendError: true });
    }
  };

  render() {
    const { navOpen, errors, sending, sent, sendError } = this.state;
    const L = this.props.currentLocale || 'en-US';
    const tx = (k, v) => t(L, k, v);
    const err = k => (errors[k] ? tx(errors[k]) : '');

    return (
      <div>
        <a className="skip" href="#main">{tx('nav.skip')}</a>

        <Header
          locale={L} current="/contact"
          navOpen={navOpen}
          onToggleNav={this.toggleNav}
          onCloseNav={this.closeNav}
          onSwitchLang={this.switchLang}
        />

        <main id="main">
          {/* ── HERO: telepon dulu, formulir belakangan ── */}
          <section className="hero" style={{ paddingBlock: 'clamp(40px,6vw,68px) clamp(44px,6vw,72px)' }}>
            <div className="wrap">
              <p className="eyebrow">{tx('nav.contact')}</p>
              <h1 style={{ fontSize: 'var(--fs-h1)', maxWidth: '20ch' }}>{tx('ct.h1')}</h1>
              <p className="lead" style={{ maxWidth: '52ch' }}>{tx('ct.lead')}</p>
              <div className="hero-cta">
                <a className="btn btn-call btn-lg" href={`tel:${PHONE}`}>
                  <IconPhone />{tx('hero.call', { phone: PHONE_LABEL })}
                </a>
                <a className="btn btn-wa btn-lg" href={WA}><IconWa />{tx('ct.whatsapp')}</a>
              </div>
              <div className="emerg" style={{ marginTop: 'var(--sp-6)', maxWidth: '56ch' }}>
                <b>{tx('ct.emerg.title')}</b>
                {tx('ct.emerg.body')}
              </div>
            </div>
          </section>

          <section>
            <div className="wrap split">
              {/* ── FORMULIR CALLBACK ── */}
              <div>
                <div className="sec-head">
                  <p className="eyebrow">{tx('ct.form.eyebrow')}</p>
                  <h2>{tx('ct.form.h2')}</h2>
                  <p className="lead">{tx('ct.form.lead')}</p>
                </div>

                <div className="form-card">
                  <form noValidate onSubmit={this.submit} style={{ display: sent ? 'none' : undefined }}>
                    <div className="two-up">
                      <div className="field">
                        <label htmlFor="cb-name">{tx('ct.f.name')}</label>
                        <input className="inp" id="cb-name" name="name" type="text"
                          autoComplete="name" value={this.state.name} onChange={this.set('name')}
                          aria-invalid={errors.name ? 'true' : undefined} required />
                        <span className="err" role="alert">{err('name')}</span>
                      </div>
                      <div className="field">
                        <label htmlFor="cb-phone">{tx('ct.f.phone')}</label>
                        <input className="inp" id="cb-phone" name="phone" type="tel"
                          inputMode="tel" autoComplete="tel" placeholder="050 505 2213"
                          value={this.state.phone} onChange={this.set('phone')}
                          aria-invalid={errors.phone ? 'true' : undefined} required />
                        <span className="err" role="alert">{err('phone')}</span>
                      </div>
                    </div>

                    <div className="field">
                      <label htmlFor="cb-where">{tx('ct.f.where')}</label>
                      <input className="inp" id="cb-where" name="where" type="text"
                        placeholder={tx('ct.f.wherePlaceholder')}
                        value={this.state.where} onChange={this.set('where')}
                        aria-invalid={errors.where ? 'true' : undefined} required />
                      <span className="hint">{tx('ct.f.whereHint')}</span>
                      <span className="err" role="alert">{err('where')}</span>
                    </div>

                    <div className="field">
                      <label id="svc-label">{tx('ct.f.service')}</label>
                      <div className="chips" role="group" aria-labelledby="svc-label">
                        {SERVICES.map(v => (
                          <button key={v} className="chip" type="button"
                            aria-pressed={String(this.state.service === v)}
                            onClick={() => this.setState({ service: v })}>
                            {tx(`ct.svc.${v}`)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="field">
                      <label htmlFor="cb-note">
                        {tx('ct.f.note')}{' '}
                        <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>{tx('ct.f.optional')}</span>
                      </label>
                      <textarea className="inp" id="cb-note" name="note" rows="3"
                        placeholder={tx('ct.f.notePlaceholder')}
                        value={this.state.note} onChange={this.set('note')} />
                    </div>

                    <button className="btn btn-call btn-lg btn-block" type="submit" disabled={sending}>
                      {sending ? tx('ct.f.sending') : tx('ct.f.submit')}
                    </button>

                    {sendError && (
                      <p className="err" role="alert" style={{ marginTop: 'var(--sp-4)', textAlign: 'center' }}>
                        {tx('ct.f.failed', { phone: PHONE_LABEL })}
                      </p>
                    )}

                    <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 'var(--sp-4)', textAlign: 'center' }}>
                      {tx('ct.f.privacy')}{' '}
                      <a href="/privacy" style={{ textDecoration: 'underline' }}>{tx('ct.f.privacyLink')}</a>.
                    </p>
                  </form>

                  {/* Hanya muncul setelah server menerima. Jangan pernah sebelum itu. */}
                  {sent && (
                    <div className="form-ok" tabIndex="-1" role="status"
                      ref={el => { this.okPanel = el; }} style={{ display: 'block' }}>
                      <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9.2" /><path d="M8 12.4l2.8 2.8L16.4 9.6" /></svg>
                      <h3>{tx('ct.ok.h3')}</h3>
                      <p style={{ marginInline: 'auto' }}>
                        {tx('ct.ok.body')}{' '}
                        <a href={`tel:${PHONE}`} style={{ color: 'var(--orange-deep)', fontWeight: 700 }}>{PHONE_LABEL}</a>{' '}
                        {tx('ct.ok.bodyEnd')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* ── SALURAN LAIN ── */}
              <div>
                <div className="sec-head">
                  <p className="eyebrow">{tx('ct.other.eyebrow')}</p>
                  <h2>{tx('ct.other.h2')}</h2>
                </div>
                <div className="cov-grid" style={{ gridTemplateColumns: '1fr' }}>
                  {CHANNELS.map(([key, value, href, hours]) => (
                    <div className="cov" key={key}>
                      <IconTick />
                      <span>{tx(`ch.${key}`)}
                        <small>
                          <a href={href}>{value || tx('nav.providers')}</a>
                          {hours ? ` · ${hours.startsWith('ch.') ? tx(hours) : hours}` : ''}
                        </small>
                      </span>
                    </div>
                  ))}
                </div>

                <div style={{
                  marginTop: 'var(--sp-6)', padding: 'var(--sp-5)', background: 'var(--bg-warm)',
                  border: '1px solid var(--line)', borderRadius: 'var(--r-lg)'
                }}>
                  <h3 style={{ fontSize: 'var(--fs-h3)', marginBottom: 'var(--sp-3)' }}>{tx('ct.prep.h3')}</h3>
                  <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>{tx('ct.prep.body')}</p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer locale={L} />
        <CallBar locale={L} />

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(LD_CONTACT) }} />
      </div>
    );
  }
}

const mapState = state => ({ currentLocale: state?.intl?.locale });
const mapDispatch = { setLocale };

export default connect(mapState, mapDispatch)(Contact);
